<script setup lang="ts">
import { useWordProcessorStore } from "~/components/Base/WordProcessor/useWordProcessorStore";

const props = defineProps<{ range?: { start: number; length: number } }>();
const documentId = inject("documentId") as string;
const wordProcessorStore = useWordProcessorStore(documentId);
const rectangles = ref<{ left: number; top: number; width: number; height: number }[]>([]);

const updateHighlight = () => {
  rectangles.value = [];
  const quill = wordProcessorStore.getQuillInstance();
  if (!quill || !props.range) return;
  const [startLeaf, startOffset] = quill.getLeaf(props.range.start);
  const [endLeaf, endOffset] = quill.getLeaf(props.range.start + props.range.length);
  if (!startLeaf?.domNode || !endLeaf?.domNode) return;
  const range = document.createRange();
  range.setStart(startLeaf.domNode, startOffset);
  range.setEnd(endLeaf.domNode, endOffset);
  const editorBounds = quill.root.getBoundingClientRect();
  rectangles.value = Array.from(range.getClientRects()).map(rect => {
    const left = Math.max(rect.left, editorBounds.left);
    const top = Math.max(rect.top, editorBounds.top);
    return {
      left, top,
      width: Math.min(rect.right, editorBounds.right) - left,
      height: Math.min(rect.bottom, editorBounds.bottom) - top,
    };
  }).filter(rect => rect.width > 0 && rect.height > 0);
};

watch(() => props.range, updateHighlight, { flush: "post" });
onMounted(() => {
  updateHighlight();
  window.addEventListener("scroll", updateHighlight, true);
  window.addEventListener("resize", updateHighlight);
});
onUnmounted(() => {
  window.removeEventListener("scroll", updateHighlight, true);
  window.removeEventListener("resize", updateHighlight);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-for="(rect, index) in rectangles"
      :key="index"
      data-correction-highlight
      aria-hidden="true"
      class="pointer-events-none fixed z-40 rounded-sm bg-blue-400/25 ring-1 ring-blue-500/40"
      :style="{ left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px` }"
    />
  </Teleport>
</template>
